import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { usePosts } from "@/hooks/usePosts";
import { BRAND, DEFAULT_PRINTABLE, formatDate, imageSrc, sectionOf, type PostFull } from "@/lib/site";
import { SignupBox } from "@/components/site/SignupBox";
import { PostCard } from "@/components/site/PostCard";
import { CardImage } from "@/components/site/CardImage";
import { PostBlocks } from "@/components/site/PostBlocks";
import { SuppliesBox } from "@/components/site/SuppliesBox";
import { ReaderPhotos } from "@/components/site/ReaderPhotos";
import { AffiliateDisclosure } from "@/lib/affiliate";

function Byline({ date }: { date: string }) {
  return (
    <div className="mt-6 flex items-center gap-3" data-testid="post-byline">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1C1917] font-['Plus_Jakarta_Sans'] text-base font-bold text-white">K</span>
      <div className="text-sm">
        <Link to="/about" className="font-bold text-[#23324A] hover:underline">By Kelly</Link>
        <p className="text-[#23324A]/60">Founder of {BRAND} · Updated {formatDate(date)}</p>
      </div>
    </div>
  );
}

function Related({ slug, section }: { slug: string; section?: string }) {
  const posts = usePosts();
  const others = (posts || []).filter((p) => p.slug !== slug);
  const pick = [...others.filter((p) => section && p.section === section), ...others.filter((p) => !section || p.section !== section)].slice(0, 2);
  return <div className="mt-6 grid gap-6 sm:grid-cols-2">{pick.map((p) => <PostCard key={p.slug} post={p} />)}</div>;
}

export default function PostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState<PostFull | null | false>(null);
  const [picks, setPicks] = useState(0);
  useEffect(() => {
    setPost(null);
    api.get(`/public/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false));
  }, [slug]);
  usePageMeta(post ? `${post.title} | ${BRAND}` : post === false ? `Page not found | ${BRAND}` : BRAND,
    post ? post.description : `Handmade gift ideas and printables from ${BRAND}.`);

  if (post === null) return <p className="mx-auto max-w-3xl px-5 pt-16 text-sm text-[#23324A]/60">Loading…</p>;
  if (post === false) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-16 sm:px-8" data-testid="not-found">
        <h1 className="font-heading text-4xl font-semibold text-[#23324A]">We couldn't find that page</h1>
        <p className="mt-4 text-[#23324A]/75">Try one of our newest gift ideas instead.</p>
        <Related slug="" />
      </div>
    );
  }
  const printable = post.printable || DEFAULT_PRINTABLE;
  const section = sectionOf(post.section);
  const hasLinks = picks > 0 || !!post.blocks?.some((b) => b.type === "project" && b.materials.length);
  const kids = post.section === "kids";

  return (
    <article className={`mx-auto max-w-3xl px-5 pt-12 sm:px-8 ${kids ? "little-hands-theme" : ""}`} data-testid="post-page">
      <div className="flex flex-wrap gap-2">
        {section && <Link to={`/gifts/${section.slug}`} className="pill hover:!bg-[#E7E5E4]" data-testid="post-section-link">{section.name}</Link>}
        {post.category && <span className="pill bg-white">{post.category}</span>}
      </div>
      <h1 className="shop-h1 mt-5 text-4xl sm:text-5xl" data-testid="post-title">{post.title}</h1>
      {kids && <p className="hand-note mt-2 -rotate-1 text-2xl">a little-hands project</p>}
      <Byline date={post.date} />
      {hasLinks && <AffiliateDisclosure className="mt-5" />}
      <figure className="relative mt-8">
        {post.design ? (
          <img src={imageSrc(post.design)} alt={post.title} className="mx-auto w-full max-w-xl rounded-2xl border border-[#E7E5E4]" data-testid="post-design-image" />
        ) : post.image ? (
          <img src={imageSrc(post.image)} alt={post.title} className="aspect-[16/10] w-full rounded-[22px] object-cover" />
        ) : (
          <div className="aspect-[16/10] w-full overflow-hidden rounded-[22px]"><CardImage post={post} /></div>
        )}
        {post.image_ai && <figcaption className={`absolute rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-[#23324A] ${post.design ? "left-1/2 top-[13%] -translate-x-1/2" : "bottom-3 left-3"}`} data-testid="hero-example-badge">Example image</figcaption>}
      </figure>
      {post.blocks ? <PostBlocks blocks={post.blocks} src={post.slug} /> : (
        <div className="wp-content mt-8" data-testid="post-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.html) }} />
      )}
      <SuppliesBox section={post.section} src={post.slug} onLoaded={setPicks} />
      <ReaderPhotos slug={post.slug} />
      <Link to={printable.to} className="calm-card mt-12 flex items-center justify-between gap-4 p-6" data-testid="post-printable-cta">
        <span>
          <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#23324A]/70">Printable</span>
          <span className="block font-heading text-xl font-semibold text-[#23324A]">{printable.title}</span>
          <span className="mt-1 block text-sm text-[#23324A]/80">{printable.text}</span>
        </span>
        <ArrowRight className="h-6 w-6 shrink-0 text-[#23324A]" strokeWidth={3} />
      </Link>
      <div className="mt-14"><SignupBox source={`post-${post.slug.slice(0, 40)}`} compact /></div>
      <h2 className="mt-16 font-heading text-xl font-semibold text-[#23324A]">More ideas{section ? ` from ${section.name}` : ""}</h2>
      <Related slug={post.slug} section={post.section} />
    </article>
  );
}
